import { useId, useState } from "react";
import { compact, count, date } from "@/lib/format";

/**
 * A y axis a person can read a value off.
 *
 * The naive version — take the data maximum, divide the height into four —
 * produced rules at 25, 19, 13 and 6, which are numbers nobody can estimate
 * against and which change every time one more review lands. This picks a
 * ROUND STEP first (1, 2, 2.5 or 5 times a power of ten, sized so there are
 * about four of them) and then extends the axis to a whole number of steps.
 * The gridlines land on 0, 10, 20, 30 and stay there for weeks.
 */
function axis(dataMax: number): { max: number; ticks: number[] } {
  const target = Math.max(dataMax, 1) / 4;
  const magnitude = 10 ** Math.floor(Math.log10(target));
  const step =
    [1, 2, 2.5, 5, 10]
      .map((multiple) => multiple * magnitude)
      .find((candidate) => candidate >= target) ?? 10 * magnitude;
  const max = step * Math.max(1, Math.ceil(Math.max(dataMax, 1) / step));
  const ticks: number[] = [];
  for (let value = 0; value <= max + step / 2; value += step) {
    ticks.push(Math.round(value));
  }
  return { max, ticks };
}

export interface TrendPoint {
  /** ISO date, the bucket's start. */
  at: string;
  value: number;
}

/**
 * The console's one real chart: a filled line over a faint grid, with a
 * hover readout.
 *
 * ── Why it is hand-drawn ─────────────────────────────────────────────────
 *
 * The same argument as `Sparkline`. One chart shape, used on two screens,
 * against a cost rule that says this whole product must stay near-free to
 * run and cheap to load. 130 lines of SVG beats a charting dependency and
 * its theme adapter.
 *
 * ── The grid ─────────────────────────────────────────────────────────────
 *
 * Four dashed horizontal rules and no verticals, at `grid` — a token one
 * step off the panel background. A grid an operator notices is a grid
 * competing with the data; its only job is to let the eye estimate a value
 * without reading the axis.
 *
 * ── The endpoint ─────────────────────────────────────────────────────────
 *
 * The last point gets a filled dot with a background-coloured ring, and its
 * value is printed above the chart. Everything else on this screen is a
 * total; the chart's job is the shape and the latest number, in that order.
 */
export function TrendChart({
  points,
  height = 168,
  label,
}: {
  points: TrendPoint[];
  height?: number;
  /** What one point counts, for the hover readout: "signups", "reviews". */
  label: string;
}) {
  const gradientId = useId();
  const [hover, setHover] = useState<number | null>(null);

  if (points.length < 2) {
    return (
      <div
        style={{ height }}
        className="flex items-center justify-center text-micro text-muted"
      >
        Not enough history yet.
      </div>
    );
  }

  const width = 720;
  const padX = 6;
  const padTop = 12;
  const padBottom = 20;
  const { max, ticks } = axis(Math.max(...points.map((p) => p.value)));
  const stepX = (width - padX * 2) / (points.length - 1);
  const plotHeight = height - padTop - padBottom;
  const y = (value: number) => padTop + plotHeight * (1 - value / max);

  const coords = points.map((p, i) => [padX + i * stepX, y(p.value)] as const);
  const line = coords.map(([px, py]) => `${px},${py}`).join(" ");
  const area = `${padX},${padTop + plotHeight} ${line} ${
    width - padX
  },${padTop + plotHeight}`;
  const last = coords[coords.length - 1] ?? [0, 0];
  const lastPoint = points[points.length - 1];

  // One rule per tick, baseline included. Any more and the grid starts to
  // read as content rather than as scaffolding.
  const rules = ticks.map((value) => ({ y: y(value), value }));

  const active = hover !== null ? points[hover] : undefined;
  const activeCoord = hover !== null ? coords[hover] : undefined;

  /**
   * Hover is read off the CONTAINER, not off one invisible rect per bucket.
   *
   * The rect-per-point version worked and cost thirty extra DOM nodes per
   * chart, none of which is a thing anybody can focus, click or tab to — an
   * interactive element that exists only for the mouse. One `mousemove` and
   * a division does the same job for any number of points.
   */
  const onMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const ratio = (event.clientX - bounds.left) / bounds.width;
    const index = Math.round(ratio * (points.length - 1));
    setHover(Math.min(points.length - 1, Math.max(0, index)));
  };

  return (
    <div className="relative">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-micro text-muted">
          {date(points[0]?.at)} — {date(lastPoint?.at)}
        </span>
        <span className="font-mono text-[12px] text-ink">
          {active ? (
            <>
              <span className="text-muted">{date(active.at)} · </span>
              {count(active.value)} {label}
            </>
          ) : (
            <>
              <span className="text-muted">latest · </span>
              {count(lastPoint?.value ?? 0)} {label}
            </>
          )}
        </span>
      </div>

      {/* biome-ignore lint/a11y/noStaticElementInteractions: a chart's hover
          readout is a mouse affordance over a graphic; the same numbers are
          in the bucket row underneath, which is what a keyboard or a screen
          reader gets. */}
      <div
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        className="cursor-crosshair"
      >
        <svg
          viewBox={`0 0 ${width} ${height}`}
          style={{ height }}
          className="w-full"
          role="img"
          aria-label={`${label} over time`}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor="rgb(var(--color-brand-green))"
                stopOpacity="0.28"
              />
              <stop
                offset="100%"
                stopColor="rgb(var(--color-brand-pink))"
                stopOpacity="0"
              />
            </linearGradient>
          </defs>

          {rules.map((rule) => (
            <g key={rule.y}>
              <line
                x1={padX}
                x2={width - padX}
                y1={rule.y}
                y2={rule.y}
                stroke="rgb(var(--color-grid))"
                strokeWidth="1"
                strokeDasharray={rule.value === 0 ? undefined : "2 4"}
              />
              <text
                x={padX + 2}
                y={rule.y - 3}
                className="fill-muted-2"
                style={{ fontSize: 9, fontFamily: "IBM Plex Mono, monospace" }}
              >
                {compact(rule.value)}
              </text>
            </g>
          ))}

          <polygon points={area} fill={`url(#${gradientId})`} />
          <polyline
            points={line}
            fill="none"
            stroke="rgb(var(--color-accent))"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {activeCoord ? (
            <>
              <line
                x1={activeCoord[0]}
                x2={activeCoord[0]}
                y1={padTop}
                y2={padTop + plotHeight}
                stroke="rgb(var(--color-muted-2))"
                strokeWidth="1"
                strokeDasharray="2 3"
              />
              <circle
                cx={activeCoord[0]}
                cy={activeCoord[1]}
                r="5"
                fill="rgb(var(--color-surface))"
                stroke="rgb(var(--color-accent))"
                strokeWidth="2.5"
              />
            </>
          ) : (
            <circle
              cx={last[0]}
              cy={last[1]}
              r="5"
              fill="rgb(var(--color-brand-green))"
              stroke="rgb(var(--color-surface))"
              strokeWidth="2"
            />
          )}

          {/* Four date ticks, evenly spaced. Not one per day: thirty labels
            at this width is a grey smear, and the reader only needs enough
            anchors to place a spike within the month. */}
          {[0, 1, 2, 3].map((tick) => {
            const index = Math.round((tick / 3) * (points.length - 1));
            const point = points[index];
            const coord = coords[index];
            if (!point || !coord) return null;
            return (
              <text
                key={`tick-${point.at}`}
                x={coord[0]}
                y={height - 5}
                textAnchor={
                  tick === 0 ? "start" : tick === 3 ? "end" : "middle"
                }
                className="fill-muted-2"
                style={{ fontSize: 9, fontFamily: "IBM Plex Mono, monospace" }}
              >
                {point.at.slice(5)}
              </text>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

/**
 * Weekly counts as bars. Same grid, same endpoint emphasis — the last bar
 * takes the accent and the rest a muted fill, because the current week is
 * the one being judged and it is also the one that is incomplete.
 */
export function BarChart({
  points,
  height = 132,
  label,
}: {
  points: TrendPoint[];
  height?: number;
  label: string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  if (points.length === 0) return null;

  const width = 720;
  const padX = 6;
  const padTop = 10;
  const padBottom = 18;
  const { max } = axis(Math.max(...points.map((p) => p.value)));
  const plotHeight = height - padTop - padBottom;
  const slot = (width - padX * 2) / points.length;
  const barWidth = Math.min(slot - 6, 34);
  const active = hover !== null ? points[hover] : undefined;

  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-micro text-muted">Last 12 weeks</span>
        <span className="font-mono text-[12px] text-ink">
          {active ? (
            <>
              <span className="text-muted">week of {date(active.at)} · </span>
              {count(active.value)} {label}
            </>
          ) : (
            <span className="text-muted">hover a week</span>
          )}
        </span>
      </div>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: see the note on
          the line chart above — hover is a mouse convenience over a graphic,
          not the only way to read the numbers. */}
      <div
        onMouseMove={(event) => {
          const bounds = event.currentTarget.getBoundingClientRect();
          const ratio = (event.clientX - bounds.left) / bounds.width;
          const index = Math.floor(ratio * points.length);
          setHover(Math.min(points.length - 1, Math.max(0, index)));
        }}
        onMouseLeave={() => setHover(null)}
        className="cursor-crosshair"
      >
        <svg
          viewBox={`0 0 ${width} ${height}`}
          style={{ height }}
          className="w-full"
          role="img"
          aria-label={`${label} by week`}
        >
          <line
            x1={padX}
            x2={width - padX}
            y1={padTop + plotHeight}
            y2={padTop + plotHeight}
            stroke="rgb(var(--color-grid))"
            strokeWidth="1"
          />
          {points.map((point, i) => {
            const barHeight = Math.max(2, (point.value / max) * plotHeight);
            const x = padX + i * slot + (slot - barWidth) / 2;
            const isLast = i === points.length - 1;
            return (
              <rect
                key={point.at}
                x={x}
                y={padTop + plotHeight - barHeight}
                width={barWidth}
                height={barHeight}
                rx="5"
                fill={
                  isLast || hover === i
                    ? "rgb(var(--color-brand-green))"
                    : "rgb(var(--color-brand-pink))"
                }
              />
            );
          })}
          {points.map((point, i) =>
            // Every other week, so labels never collide at this width.
            i % 2 === 0 ? (
              <text
                key={`label-${point.at}`}
                x={padX + i * slot + slot / 2}
                y={height - 5}
                textAnchor="middle"
                className="fill-muted-2"
                style={{ fontSize: 9, fontFamily: "IBM Plex Mono, monospace" }}
              >
                {point.at.slice(5)}
              </text>
            ) : null,
          )}
        </svg>
      </div>
    </div>
  );
}
